import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import {
  FaUsers,
  FaUserFriends,
  FaYoutube,
  FaFacebookSquare,
  FaSearch,
  FaTimes,
  FaSyncAlt,
  FaBan,
  FaUnlock,
  FaUserMinus,
  FaCrown,
  FaFileCsv,
  FaInbox,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationTriangle,
  FaRupeeSign,
  FaIdCard,
  FaLayerGroup,
  FaExternalLinkAlt,
  FaComments,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaWallet,
  FaHourglassHalf,
  FaUserPlus,
  FaSitemap,
  FaBolt,
  FaCalendarCheck,
  FaMoneyBillWave,
  FaChartPie,
} from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

/* ================= styles ================= */

const CSS = `
@keyframes gmFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
@keyframes gmFade { from { opacity: 0; } to { opacity: 1; } }
@keyframes gmZoom { from { opacity: 0; transform: scale(.95); } to { opacity: 1; transform: none; } }
@keyframes gmSlide { from { transform: translateX(100%); } to { transform: none; } }
.gm-in { animation: gmFadeUp .5s cubic-bezier(.2,.7,.2,1) both; }
.gm-fade { animation: gmFade .2s ease both; }
.gm-zoom { animation: gmZoom .25s cubic-bezier(.2,.8,.2,1) both; }
.gm-slide { animation: gmSlide .3s cubic-bezier(.2,.8,.2,1) both; }
.gm-press { transition: transform .15s ease, background-color .2s ease, border-color .2s ease, color .2s ease; }
.gm-press:hover { transform: translateY(-1px); }
.gm-press:active { transform: scale(.96); }
.gm-card { transition: transform .25s cubic-bezier(.2,.8,.2,1), box-shadow .25s ease, border-color .2s ease; }
.gm-card:hover { transform: translateY(-3px); box-shadow: 0 18px 32px -18px rgba(15,23,42,.4); }
.gm-mark { background: #fde68a; color: inherit; border-radius: 3px; padding: 0 1px; }
@media (prefers-reduced-motion: reduce) {
  .gm-in, .gm-fade, .gm-zoom, .gm-slide { animation: none; }
  .gm-press:hover, .gm-press:active, .gm-card:hover { transform: none; }
}
`;

const CARD = "bg-white dark:bg-secondary-dark-bg rounded-2xl border border-slate-200 dark:border-slate-700";
const INPUT =
  "rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500";

const BLOCK_REASONS = ["Spam or fake activity", "Did not follow group rules", "Payment issue", "Misbehaviour with other members"];
const REMOVE_REASONS = ["You are removed from the group due to policy violation.", "Membership ended", "Joined by mistake", "Duplicate membership"];

/* ================= helpers ================= */

const num = (v) => Number(v || 0);
const money = (n) => `₹${num(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const parseAny = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (d) => (d ? d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "-");
const fmtTime = (d) => (d ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "");
const daysSince = (d) => (d ? Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000)) : null);
const nm = (s) => (s && String(s).trim() ? String(s).trim() : null);
const plat = (m) => (String(m.platform || "").toLowerCase() === "facebook" ? "facebook" : String(m.platform || "").toLowerCase() === "youtube" ? "youtube" : "other");
const isBlocked = (m) => String(m.status || "").toLowerCase() === "block";
const isAdmin = (m) => String(m.role || "").toLowerCase() === "admin";
const isMasterGroup = (m) => String(m.grp_type || "").toLowerCase() === "master" || Number(m.group_id) === 1 || Number(m.group_id) === 2;
// The server adds the base URL even when there is no picture
const photoOk = (url) => url && !/\/$/.test(url) && !/\/(null|0)$/.test(url);

const Hi = ({ text, q }) => {
  const s = String(text ?? "");
  if (!q || !s) return <>{s}</>;
  const i = s.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{s}</>;
  return (
    <>
      {s.slice(0, i)}
      <mark className="gm-mark">{s.slice(i, i + q.length)}</mark>
      {s.slice(i + q.length)}
    </>
  );
};

const Avatar = ({ src, name, size = 38 }) => {
  const [err, setErr] = useState(false);
  const initials =
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";
  if (photoOk(src) && !err) return <img src={src} alt="" onError={() => setErr(true)} className="rounded-full object-cover shrink-0 bg-slate-100" style={{ width: size, height: size }} />;
  return (
    <span className="rounded-full shrink-0 flex items-center justify-center bg-slate-200 text-slate-600 font-semibold dark:bg-slate-600 dark:text-slate-200" style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </span>
  );
};

const PlatformChip = ({ m, small }) => {
  const p = plat(m);
  if (p === "youtube")
    return (
      <span className={`inline-flex items-center gap-1 font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 ${small ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"}`}>
        <FaYoutube size={small ? 9 : 10} /> YouTube
      </span>
    );
  if (p === "facebook")
    return (
      <span className={`inline-flex items-center gap-1 font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 ${small ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5"}`}>
        <FaFacebookSquare size={small ? 9 : 10} /> Facebook
      </span>
    );
  return <span className="text-xs text-slate-400">-</span>;
};

/* ================= page ================= */

const AllGroupMembers = () => {
  const [members, setMembers] = useState([]);
  const [extra, setExtra] = useState({ wallet: {}, wdPending: {}, joinPending: {}, loaded: false });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("people"); // people | memberships
  const [query, setQuery] = useState("");
  const [platform, setPlatform] = useState("all");
  const [groupF, setGroupF] = useState("all");
  const [pkgF, setPkgF] = useState("all");
  const [roleF, setRoleF] = useState("all");
  const [statusF, setStatusF] = useState("all");
  const [sort, setSort] = useState({ field: "date_time", dir: "desc" });
  const [checked, setChecked] = useState([]); // index_id
  const [page, setPage] = useState(1);
  const PAGE = 24;

  const [person, setPerson] = useState(null); // member_id
  const [confirm, setConfirm] = useState(null); // {type:'block'|'unblock'|'remove', list}
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [working, setWorking] = useState(false);

  const toast = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();
  const { currentMode } = useStateContext();

  /* ----- API (same actions) ----- */

  const post = useCallback(
    async (action, extra = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/group.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const postWallet = useCallback(
    async (action, extraArgs = {}) => {
      const fd = new FormData();
      fd.append("action", action);
      Object.entries(extraArgs).forEach(([k, v]) => fd.append(k, v));
      const res = await fetch(`${baseUrl}/wallet.php`, { method: "POST", body: fd, credentials: "include" });
      return res.json();
    },
    [baseUrl]
  );

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const [data, joins, wallets, wds] = await Promise.all([
        post("getGroupMembers"),
        post("groupJoiningRequestList").catch(() => null),
        postWallet("walletList").catch(() => null),
        postWallet("withdrawRequestList", { status: "pending" }).catch(() => null),
      ]);
      setMembers(Array.isArray(data?.members) ? data.members : []);
      const wallet = {};
      (wallets?.wallets || []).forEach((w) => {
        wallet[String(w.user_id)] = num(w.balance);
      });
      const wdPending = {};
      (wds?.withdrawals || []).forEach((r) => {
        const k = String(r.user_id);
        wdPending[k] = (wdPending[k] || 0) + num(r.amount);
      });
      const joinPending = {};
      (joins?.requests || []).forEach((r) => {
        const k = String(r.user_id);
        if (!joinPending[k]) joinPending[k] = { count: 0, amount: 0, groups: [] };
        joinPending[k].count += 1;
        joinPending[k].amount += num(r.paid_amount);
        joinPending[k].groups.push(r.grp_name || `Group ${r.group_id}`);
      });
      setExtra({ wallet, wdPending, joinPending, loaded: !!(wallets || joins || wds) });
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to fetch group members" });
    }
    setLoading(false);
  }, [post, postWallet]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const openConfirm = (type, list) => {
    setReason(type === "remove" ? REMOVE_REASONS[0] : type === "block" ? BLOCK_REASONS[0] : "");
    setCustomReason("");
    setConfirm({ type, list });
  };

  const runConfirm = async () => {
    if (!confirm) return;
    const { type, list } = confirm;
    const finalReason = (reason === "__custom" ? customReason : reason).trim();
    setWorking(true);
    let ok = 0;
    for (const m of list) {
      try {
        const res =
          type === "remove"
            ? await post("removeGroupMember", { index_id: m.index_id, reason: finalReason || REMOVE_REASONS[0] })
            : await post("updateGroupMemberStatus", { index_id: m.index_id, status: type === "block" ? "block" : "active", reason: finalReason || "Action taken by YCNP owner." });
        if (res?.error !== 0) throw new Error(res?.message || "Failed");
        ok += 1;
      } catch (e) {
        console.error(e);
      }
    }
    setWorking(false);
    setConfirm(null);
    setChecked([]);
    const verb = type === "remove" ? "removed" : type === "block" ? "blocked" : "unblocked";
    if (ok) toast.current?.show({ severity: "success", summary: "Done", detail: `${ok} membership${ok > 1 ? "s" : ""} ${verb}. Member notified.` });
    if (ok < list.length) toast.current?.show({ severity: "error", summary: "Error", detail: `${list.length - ok} could not be ${verb}` });
    fetchMembers();
  };

  /* ----- derived ----- */

  const q = query.trim();
  const nameById = useMemo(() => {
    const m = {};
    members.forEach((x) => {
      if (nm(x.member_name)) m[String(x.member_id)] = { name: x.member_name, photo: x.profile_img };
    });
    return m;
  }, [members]);

  // people each member brought in (by referral)
  const referrals = useMemo(() => {
    const m = {};
    members.forEach((x) => {
      if (x.joined_by && String(x.joined_by) !== String(x.member_id)) {
        const k = String(x.joined_by);
        if (!m[k]) m[k] = new Set();
        m[k].add(String(x.member_id));
      }
    });
    return m;
  }, [members]);

  const groupCounts = useMemo(() => {
    const m = {};
    members.forEach((x) => {
      m[String(x.group_id)] = (m[String(x.group_id)] || 0) + 1;
    });
    return m;
  }, [members]);

  // sub groups this person created (they are the Admin there)
  const owned = useMemo(() => {
    const m = {};
    members.forEach((x) => {
      if (isAdmin(x) && !isMasterGroup(x)) {
        const k = String(x.member_id);
        if (!m[k]) m[k] = [];
        m[k].push({ group_id: x.group_id, name: x.grp_name, count: groupCounts[String(x.group_id)] || 0 });
      }
    });
    return m;
  }, [members, groupCounts]);

  // one subscription per platform: the membership with the highest payment
  const subsOf = useCallback(
    (member_id) => {
      const rows = members.filter((x) => String(x.member_id) === String(member_id));
      const out = {};
      rows.forEach((x) => {
        const pl = plat(x);
        const d = parseAny(x.date_time);
        const cur = out[pl];
        if (!cur || num(x.paid_amount) > cur.paid || (!cur.pkg && x.pkg_name)) {
          out[pl] = {
            platform: pl,
            pkg: x.pkg_name || cur?.pkg || null,
            paid: Math.max(num(x.paid_amount), cur?.paid || 0),
            daily: x.daily_limit || cur?.daily,
            direct: x.direct_comission ?? cur?.direct,
            team: x.team_comission ?? cur?.team,
            since: cur?.since && d ? (d < cur.since ? d : cur.since) : d || cur?.since,
            blocked: (cur?.blocked || false) || isBlocked(x),
            rows: (cur?.rows || 0) + 1,
          };
        } else {
          cur.since = d && (!cur.since || d < cur.since) ? d : cur.since;
          cur.blocked = cur.blocked || isBlocked(x);
          cur.rows += 1;
        }
      });
      return Object.values(out).sort((a, b) => (a.platform > b.platform ? -1 : 1));
    },
    [members]
  );

  const infoOf = useCallback(
    (member_id) => {
      const k = String(member_id);
      const subs = subsOf(member_id);
      const first = subs.reduce((min, s) => (s.since && (!min || s.since < min) ? s.since : min), null);
      return {
        subs,
        totalPaid: subs.reduce((t, s2) => t + s2.paid, 0),
        wallet: extra.wallet[k],
        wdPending: extra.wdPending[k] || 0,
        join: extra.joinPending[k] || null,
        refs: referrals[k] ? referrals[k].size : 0,
        owned: owned[k] || [],
        days: daysSince(first),
        first,
      };
    },
    [subsOf, extra, referrals, owned]
  );

  const packageMix = useMemo(() => {
    const seen = {};
    members.forEach((x) => {
      if (!x.pkg_name) return;
      const key = `${x.member_id}-${plat(x)}`;
      if (!seen[key] || num(x.paid_amount) > seen[key].paid) seen[key] = { pkg: x.pkg_name, paid: num(x.paid_amount) };
    });
    const mix = {};
    Object.values(seen).forEach((v) => {
      if (!mix[v.pkg]) mix[v.pkg] = { pkg: v.pkg, people: 0, amount: 0 };
      mix[v.pkg].people += 1;
      mix[v.pkg].amount += v.paid;
    });
    return Object.values(mix).sort((a, b) => b.amount - a.amount);
  }, [members]);

  const pendingTotals = useMemo(
    () => ({
      join: Object.values(extra.joinPending).reduce((t, v) => t + v.amount, 0),
      joinCount: Object.values(extra.joinPending).reduce((t, v) => t + v.count, 0),
      wd: Object.values(extra.wdPending).reduce((t, v) => t + v, 0),
      walletHeld: Array.from(new Set(members.map((x) => String(x.member_id)))).reduce((t, id) => t + (extra.wallet[id] || 0), 0),
    }),
    [extra, members]
  );

  const options = useMemo(() => {
    const groups = {};
    const pkgs = {};
    members.forEach((m) => {
      groups[m.group_id] = m.grp_name || `Group ${m.group_id}`;
      if (m.pkg_id) pkgs[m.pkg_id] = m.pkg_name || `Package ${m.pkg_id}`;
    });
    return { groups: Object.entries(groups), pkgs: Object.entries(pkgs) };
  }, [members]);

  const matches = useCallback(
    (m) => {
      if (platform !== "all" && plat(m) !== platform) return false;
      if (groupF !== "all" && String(m.group_id) !== String(groupF)) return false;
      if (pkgF !== "all" && String(m.pkg_id) !== String(pkgF)) return false;
      if (roleF === "admin" && !isAdmin(m)) return false;
      if (roleF === "member" && isAdmin(m)) return false;
      if (statusF === "active" && isBlocked(m)) return false;
      if (statusF === "blocked" && !isBlocked(m)) return false;
      if (!q) return true;
      const ql = q.toLowerCase();
      return [m.member_name, m.member_id, m.grp_name, m.group_id, m.pkg_name, nameById[String(m.joined_by)]?.name, m.joined_by]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(ql));
    },
    [platform, groupF, pkgF, roleF, statusF, q, nameById]
  );

  const filtered = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return members.filter(matches).sort((a, b) => {
      let x;
      let y;
      if (sort.field === "date_time") {
        x = parseAny(a.date_time)?.getTime() || 0;
        y = parseAny(b.date_time)?.getTime() || 0;
      } else if (sort.field === "paid_amount") {
        x = num(a.paid_amount);
        y = num(b.paid_amount);
      } else {
        x = String(a[sort.field] ?? "").toLowerCase();
        y = String(b[sort.field] ?? "").toLowerCase();
      }
      if (x < y) return -1 * dir;
      if (x > y) return 1 * dir;
      return 0;
    });
  }, [members, matches, sort]);

  // one entry per person with all their memberships
  const people = useMemo(() => {
    const map = {};
    filtered.forEach((m) => {
      const k = String(m.member_id);
      if (!map[k]) map[k] = { member_id: m.member_id, name: m.member_name, photo: m.profile_img, rows: [], paid: 0, latest: null };
      map[k].rows.push(m);
      map[k].paid += num(m.paid_amount);
      const d = parseAny(m.date_time);
      if (d && (!map[k].latest || d > map[k].latest)) map[k].latest = d;
    });
    return Object.values(map);
  }, [filtered]);

  const listForView = view === "people" ? people : filtered;
  const totalPages = Math.max(1, Math.ceil(listForView.length / PAGE));
  const safePage = Math.min(page, totalPages);
  const pageItems = listForView.slice((safePage - 1) * PAGE, safePage * PAGE);

  useEffect(() => setPage(1), [q, platform, groupF, pkgF, roleF, statusF, view, sort]);
  useEffect(() => {
    const valid = new Set(members.map((m) => m.index_id));
    setChecked((c) => c.filter((id) => valid.has(id)));
  }, [members]);

  const stats = useMemo(() => {
    const uniq = new Set(members.map((m) => m.member_id));
    // paid amount is copied into the master group row too, so count each person's highest payment once per platform
    const paidByPersonPlatform = {};
    members.forEach((m) => {
      const k = `${m.member_id}-${plat(m)}`;
      paidByPersonPlatform[k] = Math.max(paidByPersonPlatform[k] || 0, num(m.paid_amount));
    });
    return {
      memberships: members.length,
      people: uniq.size,
      blocked: members.filter(isBlocked).length,
      admins: members.filter(isAdmin).length,
      yt: new Set(members.filter((m) => plat(m) === "youtube").map((m) => m.member_id)).size,
      fb: new Set(members.filter((m) => plat(m) === "facebook").map((m) => m.member_id)).size,
      paid: Object.values(paidByPersonPlatform).reduce((s, v) => s + v, 0),
    };
  }, [members]);

  const checkedRows = members.filter((m) => checked.includes(m.index_id));
  const hasFilters = q || platform !== "all" || groupF !== "all" || pkgF !== "all" || roleF !== "all" || statusF !== "all";
  const clearFilters = () => {
    setQuery("");
    setPlatform("all");
    setGroupF("all");
    setPkgF("all");
    setRoleF("all");
    setStatusF("all");
  };

  const toggleRows = (rows) => {
    const ids = rows.map((r) => r.index_id);
    const all = ids.every((id) => checked.includes(id));
    setChecked((c) => (all ? c.filter((id) => !ids.includes(id)) : Array.from(new Set([...c, ...ids]))));
  };

  const toggleSort = (field) => setSort((s) => ({ field, dir: s.field === field && s.dir === "desc" ? "asc" : "desc" }));

  useEffect(() => {
    const onKey = (e) => {
      const typing = ["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName);
      if (confirm) {
        if (e.key === "Escape" && !working) setConfirm(null);
        return;
      }
      if (person && e.key === "Escape") setPerson(null);
      if (!typing && e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [confirm, working, person]);

  const exportCsv = () => {
    const head = ["Member ID", "Member", "Group", "Group ID", "Platform", "Package", "Paid", "Wallet", "Withdraw Pending", "Join Pending", "Role", "Joined By", "Status", "Joined Date"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = filtered.map((m) =>
      [m.member_id, nm(m.member_name) || "Unknown", m.grp_name, m.group_id, m.platform, m.pkg_name, num(m.paid_amount), extra.wallet[String(m.member_id)] ?? "", extra.wdPending[String(m.member_id)] || 0, extra.joinPending[String(m.member_id)]?.amount || 0, m.role, nameById[String(m.joined_by)]?.name || m.joined_by, m.status, m.date_time].map(esc).join(",")
    );
    const csv = "\uFEFF" + [head.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ycnp-group-members-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ----- pieces ----- */

  const membershipActions = (m, compact) => (
    <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => openConfirm(isBlocked(m) ? "unblock" : "block", [m])}
        title={isBlocked(m) ? "Unblock in this group" : "Block in this group"}
        className={`gm-press inline-flex items-center justify-center gap-1.5 h-8 ${compact ? "w-8" : "px-3"} rounded-lg text-xs font-semibold ${isBlocked(m) ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-amber-50 text-amber-700 hover:bg-amber-100"}`}
      >
        {isBlocked(m) ? <FaUnlock size={10} /> : <FaBan size={10} />} {!compact && (isBlocked(m) ? "Unblock" : "Block")}
      </button>
      <button type="button" onClick={() => openConfirm("remove", [m])} title="Remove from this group" aria-label="Remove from this group" className="gm-press w-8 h-8 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white flex items-center justify-center">
        <FaUserMinus size={11} />
      </button>
    </div>
  );

  const groupChip = (m) => (
    <span
      key={m.index_id}
      className={`inline-flex items-center gap-1.5 max-w-full text-xs rounded-lg border px-2 py-1 ${
        isBlocked(m) ? "border-rose-200 bg-rose-50 text-rose-700 line-through decoration-rose-400" : plat(m) === "facebook" ? "border-blue-200 bg-blue-50 text-blue-800" : "border-red-200 bg-red-50 text-red-800"
      } dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200`}
      title={`${m.grp_name} • ${m.pkg_name || "No package"}${isBlocked(m) ? " • blocked" : ""}`}
    >
      {plat(m) === "facebook" ? <FaFacebookSquare size={10} /> : <FaYoutube size={10} />}
      {isMasterGroup(m) && <FaCrown size={9} className="text-amber-500" />}
      <span className="truncate">
        <Hi text={m.grp_name || `Group ${m.group_id}`} q={q} />
      </span>
      {isAdmin(m) && <span className="text-[9px] font-bold px-1 rounded bg-slate-900 text-white">ADMIN</span>}
    </span>
  );

  const SortTh = ({ field, children, right }) => {
    const active = sort.field === field;
    const Icon = !active ? FaSort : sort.dir === "asc" ? FaSortUp : FaSortDown;
    return (
      <th className={`px-3 py-3 font-medium whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
        <button type="button" onClick={() => toggleSort(field)} className="inline-flex items-center gap-1.5 hover:text-slate-900 dark:hover:text-white">
          {children}
          <Icon size={10} className={active ? "text-cyan-600" : "text-slate-300"} />
        </button>
      </th>
    );
  };

  const personData = person ? members.filter((m) => String(m.member_id) === String(person)) : [];
  const personHead = personData[0];

  /* ================= render ================= */

  return (
    <div style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}} className="w-full p-4 sm:p-6 lg:p-8 bg-slate-50 min-h-screen">
      <style>{CSS}</style>
      <Toast ref={toast} />

      {/* Header */}
      <div className="gm-in flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">Management</p>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">All Group Members</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Every person in every group. One person can be in the master group and a sub group at the same time.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate("/group/list")} className="gm-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200">
            <FaLayerGroup size={12} /> Groups
          </button>
          <button type="button" onClick={exportCsv} disabled={!filtered.length} className="gm-press inline-flex items-center gap-2 h-9 px-3.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg text-sm font-medium text-slate-700 dark:text-slate-200 disabled:opacity-50">
            <FaFileCsv /> Export
          </button>
          <button type="button" onClick={fetchMembers} disabled={loading} className="gm-press inline-flex items-center gap-2 h-9 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-60">
            <FaSyncAlt size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: "People", value: stats.people, icon: FaUsers, tint: "bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300", onClick: () => setView("people") },
          { label: "Memberships", value: stats.memberships, icon: FaIdCard, tint: "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300", onClick: () => setView("memberships") },
          { label: "YouTube / Facebook people", value: `${stats.yt} / ${stats.fb}`, icon: FaYoutube, tint: "bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300", onClick: () => setPlatform(platform === "youtube" ? "facebook" : "youtube") },
          { label: "Blocked", value: stats.blocked, icon: FaBan, tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300", onClick: () => setStatusF("blocked") },
          { label: "Joining fees paid", value: money(stats.paid), icon: FaRupeeSign, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300", onClick: () => setSort({ field: "paid_amount", dir: "desc" }) },
        ].map((c, i) => {
          const Icon = c.icon;
          return (
            <button key={c.label} type="button" onClick={c.onClick} className={`${CARD} gm-in gm-press p-4 flex items-center gap-3 text-left`} style={{ animationDelay: `${i * 50}ms` }}>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.tint}`}>
                <Icon />
              </span>
              <div className="min-w-0">
                <p className="text-xl font-bold text-slate-900 dark:text-white tabular-nums truncate">{loading && !members.length ? "–" : c.value}</p>
                <p className="text-xs text-slate-500 truncate">{c.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Money + package overview */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mt-3">
        <div className={`${CARD} gm-in p-4 xl:col-span-2`} style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2 mb-3">
            <FaChartPie className="text-violet-500" />
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Who bought which package</h2>
            <span className="text-xs text-slate-500">one count per person per platform</span>
          </div>
          {packageMix.length ? (
            <>
              <div className="flex h-3 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700 gap-0.5">
                {packageMix.map((p, i) => (
                  <button
                    key={p.pkg}
                    type="button"
                    title={`${p.pkg}: ${p.people} people`}
                    onClick={() => setPkgF(options.pkgs.find(([, n]) => n === p.pkg)?.[0] || "all")}
                    className={`h-full ${["bg-violet-500", "bg-cyan-500", "bg-amber-500", "bg-emerald-500", "bg-rose-500", "bg-indigo-500", "bg-slate-500"][i % 7]}`}
                    style={{ width: `${(p.people / packageMix.reduce((t, x) => t + x.people, 0)) * 100}%` }}
                  />
                ))}
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {packageMix.map((p, i) => (
                  <button
                    key={p.pkg}
                    type="button"
                    onClick={() => setPkgF(options.pkgs.find(([, n]) => n === p.pkg)?.[0] || "all")}
                    className="gm-press text-left rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2 hover:border-slate-400"
                  >
                    <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${["bg-violet-500", "bg-cyan-500", "bg-amber-500", "bg-emerald-500", "bg-rose-500", "bg-indigo-500", "bg-slate-500"][i % 7]}`} />
                      {p.pkg}
                    </p>
                    <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{p.people} people</p>
                    <p className="text-[11px] text-emerald-700 dark:text-emerald-400 tabular-nums">{money(p.amount)} paid</p>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-500">No package data yet</p>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3">
          {[
            { label: "Join / upgrade requests waiting", value: money(pendingTotals.join), sub: `${pendingTotals.joinCount} request${pendingTotals.joinCount === 1 ? "" : "s"}`, icon: FaUserPlus, tint: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300", to: "/group/join-request" },
            { label: "Withdrawals waiting", value: money(pendingTotals.wd), sub: "to be paid out", icon: FaMoneyBillWave, tint: "bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300", to: "/wallet/withdrawal-req" },
            { label: "Money in members' wallets", value: money(pendingTotals.walletHeld), sub: "group earnings not withdrawn", icon: FaWallet, tint: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300", to: "/group/wallet" },
          ].map((c, i) => {
            const Icon = c.icon;
            return (
              <button key={c.label} type="button" onClick={() => navigate(c.to)} className={`${CARD} gm-in gm-press px-4 py-3 flex items-center gap-3 text-left`} style={{ animationDelay: `${240 + i * 50}ms` }}>
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${c.tint}`}>
                  <Icon />
                </span>
                <div className="min-w-0">
                  <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white">{extra.loaded ? c.value : "–"}</p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {c.label} • {c.sub}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search + filters */}
      <div className={`${CARD} gm-in mt-4 p-4`} style={{ animationDelay: "240ms" }}>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search member, ID, group, package or referrer" className={`${INPUT} w-full h-12 pl-11 pr-12 text-base`} />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              {query ? (
                <button type="button" onClick={() => setQuery("")} className="p-1.5 text-slate-400 hover:text-slate-700" aria-label="Clear search">
                  <FaTimes size={13} />
                </button>
              ) : (
                <kbd className="hidden sm:inline text-xs text-slate-400 border border-slate-200 dark:border-slate-600 rounded px-1.5 py-0.5">/</kbd>
              )}
            </div>
          </div>
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700 self-start">
            {[
              { id: "people", label: "People", icon: FaUserFriends },
              { id: "memberships", label: "Memberships", icon: FaIdCard },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <button key={v.id} type="button" onClick={() => setView(v.id)} className={`inline-flex items-center gap-1.5 h-10 px-3.5 rounded-lg text-sm font-medium transition-all active:scale-95 ${view === v.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                  <Icon size={12} /> {v.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-700">
            {[
              { id: "all", label: "All" },
              { id: "youtube", label: "YouTube", icon: FaYoutube, cls: "text-red-600" },
              { id: "facebook", label: "Facebook", icon: FaFacebookSquare, cls: "text-blue-600" },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} type="button" onClick={() => setPlatform(t.id)} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium transition-all active:scale-95 ${platform === t.id ? "bg-white dark:bg-secondary-dark-bg text-slate-900 dark:text-white shadow-sm" : "text-slate-500 dark:text-slate-300"}`}>
                  {Icon && <Icon size={11} className={t.cls} />} {t.label}
                </button>
              );
            })}
          </div>
          <select value={groupF} onChange={(e) => setGroupF(e.target.value)} className={`${INPUT} h-9 px-2 max-w-[200px]`}>
            <option value="all">All groups</option>
            {options.groups.map(([id, n]) => (
              <option key={id} value={id}>
                {n}
              </option>
            ))}
          </select>
          <select value={pkgF} onChange={(e) => setPkgF(e.target.value)} className={`${INPUT} h-9 px-2 max-w-[180px]`}>
            <option value="all">All packages</option>
            {options.pkgs.map(([id, n]) => (
              <option key={id} value={id}>
                {n}
              </option>
            ))}
          </select>
          <select value={roleF} onChange={(e) => setRoleF(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="all">Any role</option>
            <option value="admin">Group admins</option>
            <option value="member">Members</option>
          </select>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className={`${INPUT} h-9 px-2`}>
            <option value="all">Any status</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-cyan-700 dark:text-cyan-400 hover:underline">
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Result + bulk bar */}
      <div className="flex flex-wrap items-center gap-3 mt-4 mb-3">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {view === "people" ? (
            <>
              <b>{people.length}</b> people in <b>{filtered.length}</b> memberships
            </>
          ) : (
            <>
              <b>{filtered.length}</b> memberships
            </>
          )}
          {q && (
            <>
              {" "}
              for "<b>{q}</b>"
            </>
          )}
        </p>
        {checked.length > 0 && (
          <div className="gm-fade ml-auto flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{checked.length} memberships selected</span>
            <button type="button" onClick={() => openConfirm("block", checkedRows.filter((m) => !isBlocked(m)))} disabled={!checkedRows.some((m) => !isBlocked(m))} className="gm-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-40">
              <FaBan size={11} /> Block
            </button>
            <button type="button" onClick={() => openConfirm("unblock", checkedRows.filter(isBlocked))} disabled={!checkedRows.some(isBlocked)} className="gm-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-40">
              <FaUnlock size={11} /> Unblock
            </button>
            <button type="button" onClick={() => openConfirm("remove", checkedRows)} className="gm-press inline-flex items-center gap-1.5 h-9 px-3 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold">
              <FaUserMinus size={11} /> Remove
            </button>
            <button type="button" onClick={() => setChecked([])} className="text-sm text-slate-500 hover:underline">
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {loading && !members.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={`${CARD} h-44 animate-pulse`} />
          ))}
        </div>
      ) : !listForView.length ? (
        <div className={`${CARD} px-5 py-16 text-center`}>
          <FaInbox className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-semibold text-slate-700 dark:text-slate-200">{members.length ? "No member matches these filters" : "No group members yet"}</p>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="mt-2 text-sm font-medium text-cyan-700 hover:underline">
              Clear all filters
            </button>
          )}
        </div>
      ) : view === "people" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-4">
          {pageItems.map((p, i) => {
            const allSel = p.rows.every((r) => checked.includes(r.index_id));
            const anyBlocked = p.rows.some(isBlocked);
            const ref = p.rows.find((r) => r.joined_by && String(r.joined_by) !== String(r.member_id));
            const inf = infoOf(p.member_id);
            const activeCount = p.rows.filter((r) => !isBlocked(r)).length;
            const ownedMembers = inf.owned.reduce((t, g) => t + g.count, 0);
            return (
              <article
                key={p.member_id}
                onClick={() => setPerson(p.member_id)}
                className={`gm-card gm-in cursor-pointer rounded-2xl border bg-white dark:bg-secondary-dark-bg overflow-hidden flex flex-col ${allSel ? "border-cyan-500 ring-2 ring-cyan-500/30" : anyBlocked ? "border-rose-200 dark:border-rose-900" : "border-slate-200 dark:border-slate-700"}`}
                style={{ animationDelay: `${Math.min(i, 12) * 35}ms` }}
              >
                {/* Head */}
                <div className="p-4 pb-3 flex items-start gap-3">
                  <input type="checkbox" checked={allSel} onClick={(e) => e.stopPropagation()} onChange={() => toggleRows(p.rows)} className="mt-3 w-4 h-4 accent-cyan-600 cursor-pointer" aria-label="Select all memberships of this person" />
                  <div className="relative">
                    <Avatar src={p.photo} name={p.name} size={50} />
                    <span className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ring-2 ring-white dark:ring-slate-800 ${activeCount === p.rows.length ? "bg-emerald-500" : activeCount === 0 ? "bg-rose-500" : "bg-amber-500"}`} title={`${activeCount} of ${p.rows.length} memberships active`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold truncate ${nm(p.name) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>
                      <Hi text={nm(p.name) || "Unknown user"} q={q} />
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      ID <Hi text={p.member_id} q={q} />
                    </p>
                    <p className={`text-[11px] font-semibold mt-0.5 ${activeCount === p.rows.length ? "text-emerald-600" : activeCount === 0 ? "text-rose-600" : "text-amber-600"}`}>
                      Active in {activeCount} of {p.rows.length} group{p.rows.length > 1 ? "s" : ""}
                      {inf.days !== null && <span className="text-slate-400 font-normal"> • member for {inf.days} days</span>}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-slate-500">Total paid</p>
                    <p className="text-xl font-black tabular-nums text-slate-900 dark:text-white">{money(inf.totalPaid)}</p>
                  </div>
                </div>

                {/* Subscriptions */}
                <div className="px-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {inf.subs.map((sb) => (
                    <div
                      key={sb.platform}
                      className={`rounded-xl p-3 border ${sb.blocked ? "border-rose-200 bg-rose-50/60 dark:border-rose-900 dark:bg-rose-900/10" : sb.platform === "facebook" ? "border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-900/10" : "border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-900/10"}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${sb.platform === "facebook" ? "text-blue-700 dark:text-blue-300" : "text-red-700 dark:text-red-300"}`}>
                          {sb.platform === "facebook" ? <FaFacebookSquare /> : <FaYoutube />} {sb.platform === "facebook" ? "Facebook" : "YouTube"}
                        </span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sb.blocked ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"}`}>{sb.blocked ? "BLOCKED" : "ACTIVE"}</span>
                      </div>
                      <p className="mt-1.5 text-sm font-bold text-slate-900 dark:text-white truncate">
                        <Hi text={sb.pkg || "No package"} q={q} />
                      </p>
                      <p className="text-lg font-black tabular-nums text-slate-900 dark:text-white leading-tight">{money(sb.paid)}</p>
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600 dark:text-slate-300">
                        {sb.daily && (
                          <span className="inline-flex items-center gap-1">
                            <FaBolt size={9} className="text-amber-500" /> {sb.daily}/day
                          </span>
                        )}
                        {sb.direct !== undefined && sb.direct !== null && (
                          <span>
                            {sb.direct}% / {sb.team}% comm.
                          </span>
                        )}
                        {sb.since && (
                          <span className="inline-flex items-center gap-1">
                            <FaCalendarCheck size={9} /> {daysSince(sb.since)}d
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Money + network */}
                <div className="px-4 mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-emerald-50 dark:bg-emerald-900/20 p-2">
                    <p className="text-[10px] text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
                      <FaWallet size={9} /> Wallet
                    </p>
                    <p className="text-sm font-bold tabular-nums text-emerald-800 dark:text-emerald-200">{inf.wallet !== undefined ? money(inf.wallet) : "-"}</p>
                  </div>
                  <div className={`rounded-xl p-2 ${inf.wdPending ? "bg-rose-50 dark:bg-rose-900/20" : "bg-slate-50 dark:bg-slate-800/60"}`}>
                    <p className={`text-[10px] inline-flex items-center gap-1 ${inf.wdPending ? "text-rose-700" : "text-slate-500"}`}>
                      <FaMoneyBillWave size={9} /> Withdraw
                    </p>
                    <p className={`text-sm font-bold tabular-nums ${inf.wdPending ? "text-rose-700" : "text-slate-400"}`}>{inf.wdPending ? money(inf.wdPending) : "None"}</p>
                  </div>
                  <div className={`rounded-xl p-2 ${inf.join ? "bg-amber-50 dark:bg-amber-900/20" : "bg-slate-50 dark:bg-slate-800/60"}`} title={inf.join ? inf.join.groups.join(", ") : ""}>
                    <p className={`text-[10px] inline-flex items-center gap-1 ${inf.join ? "text-amber-700" : "text-slate-500"}`}>
                      <FaHourglassHalf size={9} /> Join/upgrade
                    </p>
                    <p className={`text-sm font-bold tabular-nums ${inf.join ? "text-amber-700" : "text-slate-400"}`}>{inf.join ? money(inf.join.amount) : "None"}</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2">
                    <p className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                      <FaUserFriends size={9} /> Referred
                    </p>
                    <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white">{inf.refs} people</p>
                  </div>
                  <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2 col-span-2" title={inf.owned.map((g) => `${g.name} (${g.count})`).join(", ")}>
                    <p className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                      <FaSitemap size={9} /> Own sub groups
                    </p>
                    <p className="text-sm font-bold tabular-nums text-slate-900 dark:text-white truncate">
                      {inf.owned.length ? `${inf.owned.length} group${inf.owned.length > 1 ? "s" : ""} • ${ownedMembers} members` : "None"}
                    </p>
                  </div>
                </div>

                {/* Groups */}
                <div className="px-4 mt-3 flex flex-wrap gap-1.5">{p.rows.map(groupChip)}</div>

                <div className="mt-auto pt-3"><div className="px-4 py-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between gap-2 text-xs text-slate-500">
                  <span className="truncate">{ref ? <>Joined via <b className="text-slate-700 dark:text-slate-200">{nameById[String(ref.joined_by)]?.name || ref.joined_by}</b></> : "Joined directly"}</span>
                  <span className="shrink-0">First joined {fmtDate(inf.first || p.latest)}</span>
                </div></div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className={`${CARD} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="pl-4 py-3 w-8">
                    <input type="checkbox" checked={pageItems.length > 0 && pageItems.every((m) => checked.includes(m.index_id))} onChange={() => toggleRows(pageItems)} className="w-4 h-4 accent-cyan-600 cursor-pointer" aria-label="Select page" />
                  </th>
                  <SortTh field="member_name">Member</SortTh>
                  <SortTh field="grp_name">Group</SortTh>
                  <SortTh field="platform">Platform</SortTh>
                  <SortTh field="pkg_name">Package</SortTh>
                  <SortTh field="paid_amount" right>Paid</SortTh>
                  <th className="px-3 py-3 font-medium text-right">Wallet</th>
                  <th className="px-3 py-3 font-medium text-left">Pending</th>
                  <SortTh field="role">Role</SortTh>
                  <th className="px-3 py-3 font-medium text-left whitespace-nowrap">Joined By</th>
                  <SortTh field="date_time">Joined Date</SortTh>
                  <SortTh field="status">Status</SortTh>
                  <th className="px-3 py-3 pr-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {pageItems.map((m) => {
                  const d = parseAny(m.date_time);
                  const ref = String(m.joined_by) !== String(m.member_id) ? nameById[String(m.joined_by)]?.name || m.joined_by : null;
                  return (
                    <tr key={m.index_id} onClick={() => setPerson(m.member_id)} className={`cursor-pointer transition-colors ${checked.includes(m.index_id) ? "bg-cyan-50/60 dark:bg-cyan-900/10" : "hover:bg-slate-50 dark:hover:bg-slate-700/40"}`}>
                      <td className="pl-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={checked.includes(m.index_id)} onChange={() => toggleRows([m])} className="w-4 h-4 accent-cyan-600 cursor-pointer" aria-label="Select membership" />
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2.5 min-w-[170px]">
                          <Avatar src={m.profile_img} name={m.member_name} size={34} />
                          <div className="min-w-0">
                            <p className={`font-medium truncate ${nm(m.member_name) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>
                              <Hi text={nm(m.member_name) || "Unknown user"} q={q} />
                            </p>
                            <p className="text-xs text-slate-500 tabular-nums">
                              <Hi text={m.member_id} q={q} />
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 min-w-[150px]">
                        <p className="text-slate-800 dark:text-slate-100 inline-flex items-center gap-1.5">
                          {isMasterGroup(m) && <FaCrown size={10} className="text-amber-500" />}
                          <Hi text={m.grp_name || `Group ${m.group_id}`} q={q} />
                        </p>
                        <p className="text-[11px] text-slate-500">{isMasterGroup(m) ? "Master" : "Sub group"}</p>
                      </td>
                      <td className="px-3 py-3">
                        <PlatformChip m={m} />
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {m.pkg_name ? (
                          <span className="text-xs font-semibold px-2 py-1 rounded-lg bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
                            <Hi text={m.pkg_name} q={q} />
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">{num(m.paid_amount) ? money(m.paid_amount) : "-"}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-emerald-700 dark:text-emerald-400">{extra.wallet[String(m.member_id)] !== undefined ? money(extra.wallet[String(m.member_id)]) : "-"}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {extra.joinPending[String(m.member_id)] && (
                          <span className="block text-[11px] font-semibold text-amber-700">Join {money(extra.joinPending[String(m.member_id)].amount)}</span>
                        )}
                        {extra.wdPending[String(m.member_id)] ? <span className="block text-[11px] font-semibold text-rose-600">Withdraw {money(extra.wdPending[String(m.member_id)])}</span> : null}
                        {!extra.joinPending[String(m.member_id)] && !extra.wdPending[String(m.member_id)] && <span className="text-slate-400">-</span>}
                      </td>
                      <td className="px-3 py-3">
                        {isAdmin(m) ? <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-900 text-white dark:bg-white dark:text-slate-900">Admin</span> : <span className="text-slate-600 dark:text-slate-300">Member</span>}
                      </td>
                      <td className="px-3 py-3 text-slate-600 dark:text-slate-300 max-w-[140px] truncate">{ref ? <Hi text={ref} q={q} /> : <span className="text-slate-400">Direct</span>}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="text-slate-700 dark:text-slate-200">{fmtDate(d)}</div>
                        <div className="text-xs text-slate-500">{fmtTime(d)}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-full ${isBlocked(m) ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isBlocked(m) ? "bg-rose-500" : "bg-emerald-500"}`} />
                          {isBlocked(m) ? "Blocked" : "Active"}
                        </span>
                      </td>
                      <td className="px-3 py-3 pr-4">
                        <div className="flex justify-end">{membershipActions(m, true)}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {listForView.length > PAGE && (
        <div className="flex items-center justify-center gap-3 mt-5 text-sm">
          <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1} className="gm-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Previous page">
            <FaChevronLeft size={11} />
          </button>
          <span className="tabular-nums text-slate-600 dark:text-slate-300">
            Page <b>{safePage}</b> of {totalPages}
          </span>
          <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages} className="gm-press w-10 h-10 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-secondary-dark-bg flex items-center justify-center disabled:opacity-40" aria-label="Next page">
            <FaChevronRight size={11} />
          </button>
        </div>
      )}

      {/* ================= Person drawer ================= */}
      {person && personHead && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 1100 }}>
          <div className="gm-fade absolute inset-0 bg-slate-900/40" onClick={() => setPerson(null)} />
          <aside className="gm-slide relative w-full max-w-lg h-full bg-white dark:bg-secondary-dark-bg shadow-2xl flex flex-col">
            <div className="flex items-start gap-4 px-5 py-5 border-b border-slate-200 dark:border-slate-700">
              <Avatar src={personHead.profile_img} name={personHead.member_name} size={64} />
              <div className="flex-1 min-w-0">
                <h2 className={`text-xl font-semibold truncate ${nm(personHead.member_name) ? "text-slate-900 dark:text-white" : "text-slate-400 italic"}`}>{nm(personHead.member_name) || "Unknown user"}</h2>
                <p className="text-sm text-slate-500 tabular-nums">ID {personHead.member_id}</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" onClick={() => navigate(`/user-profile/${personHead.member_id}`)} className="gm-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Profile <FaExternalLinkAlt size={9} />
                  </button>
                  <button type="button" onClick={() => navigate(`/wallet-account/${personHead.member_id}`)} className="gm-press inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Wallet <FaExternalLinkAlt size={9} />
                  </button>
                </div>
              </div>
              <button type="button" onClick={() => setPerson(null)} className="gm-press w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Close">
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {(() => {
                const inf = infoOf(personHead.member_id);
                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                    {[
                      { label: "Total paid", value: money(inf.totalPaid), tone: "text-slate-900 dark:text-white" },
                      { label: "Wallet", value: inf.wallet !== undefined ? money(inf.wallet) : "-", tone: "text-emerald-700 dark:text-emerald-400" },
                      { label: "Withdraw pending", value: inf.wdPending ? money(inf.wdPending) : "None", tone: inf.wdPending ? "text-rose-600" : "text-slate-400" },
                      { label: "Join / upgrade pending", value: inf.join ? `${money(inf.join.amount)} (${inf.join.count})` : "None", tone: inf.join ? "text-amber-700" : "text-slate-400" },
                      { label: "People referred", value: inf.refs, tone: "text-slate-900 dark:text-white" },
                      { label: "Member for", value: inf.days !== null ? `${inf.days} days` : "-", tone: "text-slate-900 dark:text-white" },
                    ].map((x) => (
                      <div key={x.label} className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-2.5">
                        <p className="text-[10px] text-slate-500">{x.label}</p>
                        <p className={`text-sm font-bold tabular-nums ${x.tone}`}>{x.value}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <p className="text-xs font-semibold text-slate-500">Member of {personData.length} group{personData.length > 1 ? "s" : ""}</p>
              {personData.map((m) => {
                const d = parseAny(m.date_time);
                const ref = String(m.joined_by) !== String(m.member_id) ? nameById[String(m.joined_by)]?.name || m.joined_by : null;
                return (
                  <div key={m.index_id} className={`rounded-2xl border p-4 ${isBlocked(m) ? "border-rose-200 bg-rose-50/50 dark:border-rose-900 dark:bg-rose-900/10" : "border-slate-200 dark:border-slate-700"}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 dark:text-white inline-flex items-center gap-1.5">
                          {isMasterGroup(m) && <FaCrown size={11} className="text-amber-500" />}
                          {m.grp_name || `Group ${m.group_id}`}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <PlatformChip m={m} small />
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">{isMasterGroup(m) ? "Master" : "Sub group"}</span>
                          {isAdmin(m) && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-900 text-white">ADMIN</span>}
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isBlocked(m) ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"}`}>{isBlocked(m) ? "BLOCKED" : "ACTIVE"}</span>
                        </div>
                      </div>
                      {membershipActions(m)}
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                        <dt className="text-slate-500">Package</dt>
                        <dd className="font-semibold text-slate-900 dark:text-white">{m.pkg_name || "-"}</dd>
                      </div>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                        <dt className="text-slate-500">Paid</dt>
                        <dd className="font-semibold text-slate-900 dark:text-white tabular-nums">{num(m.paid_amount) ? money(m.paid_amount) : "-"}</dd>
                      </div>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                        <dt className="text-slate-500">Joined by</dt>
                        <dd className="font-semibold text-slate-900 dark:text-white truncate">{ref || "Direct"}</dd>
                      </div>
                      <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                        <dt className="text-slate-500">Joined on</dt>
                        <dd className="font-semibold text-slate-900 dark:text-white">
                          {fmtDate(d)}, {fmtTime(d)}
                        </dd>
                      </div>
                      {m.daily_limit && (
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                          <dt className="text-slate-500">Daily limit</dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">{m.daily_limit}/day</dd>
                        </div>
                      )}
                      {m.direct_comission && (
                        <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-2">
                          <dt className="text-slate-500">Commission</dt>
                          <dd className="font-semibold text-slate-900 dark:text-white">
                            {m.direct_comission}% direct, {m.team_comission}% team
                          </dd>
                        </div>
                      )}
                    </dl>
                    <button type="button" onClick={() => navigate(`/group-chat/${m.group_id}`)} className="mt-2 text-xs font-medium text-cyan-700 dark:text-cyan-400 hover:underline inline-flex items-center gap-1">
                      <FaComments size={10} /> Open group chat
                    </button>
                  </div>
                );
              })}
            </div>
            {personData.length > 1 && (
              <div className="grid grid-cols-2 gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                <button type="button" onClick={() => openConfirm("block", personData.filter((m) => !isBlocked(m)))} disabled={!personData.some((m) => !isBlocked(m))} className="gm-press h-10 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                  <FaBan size={11} /> Block in all groups
                </button>
                <button type="button" onClick={() => openConfirm("remove", personData)} className="gm-press h-10 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
                  <FaUserMinus size={11} /> Remove from all
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      {/* ================= Confirm ================= */}
      {confirm && confirm.list.length > 0 && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 1200 }}>
          <div className="gm-fade absolute inset-0 bg-slate-900/60" onClick={() => !working && setConfirm(null)} />
          <div className="gm-zoom relative w-full max-w-md rounded-2xl bg-white dark:bg-secondary-dark-bg shadow-2xl p-5">
            <span className={`w-12 h-12 rounded-2xl flex items-center justify-center ${confirm.type === "unblock" ? "bg-emerald-100 text-emerald-600" : "bg-rose-100 text-rose-600"}`}>
              {confirm.type === "remove" ? <FaExclamationTriangle size={18} /> : confirm.type === "block" ? <FaBan size={16} /> : <FaUnlock size={16} />}
            </span>
            <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">
              {confirm.type === "remove" ? "Remove from group?" : confirm.type === "block" ? "Block in group?" : "Unblock in group?"}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              {confirm.type === "remove"
                ? "The membership is deleted. They will have to join again (and pay again) to come back."
                : confirm.type === "block"
                ? "They stay in the group but cannot take part. Their app account is not blocked."
                : "They can take part in the group again."}{" "}
              They get a notification.
            </p>
            <div className="mt-4 max-h-40 overflow-y-auto rounded-xl bg-slate-50 dark:bg-slate-800/60 divide-y divide-slate-100 dark:divide-slate-700">
              {confirm.list.map((m) => (
                <div key={m.index_id} className="flex items-center gap-3 p-2.5">
                  <Avatar src={m.profile_img} name={m.member_name} size={28} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{nm(m.member_name) || m.member_id}</p>
                    <p className="text-xs text-slate-500 truncate">{m.grp_name}</p>
                  </div>
                  {isMasterGroup(m) && <FaCrown size={11} className="text-amber-500" />}
                </div>
              ))}
            </div>
            {confirm.type === "remove" && confirm.list.some(isMasterGroup) && (
              <p className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs p-3">
                <FaExclamationTriangle className="mt-0.5 shrink-0" /> This includes a master group. Removing from the master group ends their monetization membership for that platform.
              </p>
            )}
            {confirm.type !== "unblock" && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">Reason (sent to the member)</p>
                <div className="flex flex-wrap gap-2">
                  {[...(confirm.type === "remove" ? REMOVE_REASONS : BLOCK_REASONS), "__custom"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setReason(r)}
                      className={`gm-press h-8 px-3 rounded-full text-xs font-semibold border max-w-full truncate ${reason === r ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"}`}
                    >
                      {r === "__custom" ? "Write my own" : r}
                    </button>
                  ))}
                </div>
                {reason === "__custom" && <textarea rows={2} value={customReason} onChange={(e) => setCustomReason(e.target.value)} autoFocus placeholder="Write the reason" className={`${INPUT} w-full mt-2 px-3 py-2`} />}
              </div>
            )}
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setConfirm(null)} disabled={working} className="gm-press flex-1 h-11 rounded-xl border border-slate-200 dark:border-slate-600 text-sm font-medium text-slate-700 dark:text-slate-200">
                Go back
              </button>
              <button
                type="button"
                onClick={runConfirm}
                disabled={working || (reason === "__custom" && !customReason.trim())}
                className={`gm-press flex-1 h-11 rounded-xl text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 ${confirm.type === "unblock" ? "bg-emerald-600 hover:bg-emerald-700" : confirm.type === "block" ? "bg-amber-600 hover:bg-amber-700" : "bg-rose-600 hover:bg-rose-700"}`}
              >
                {working && <FaSyncAlt className="animate-spin" size={12} />}
                {working ? "Please wait..." : confirm.type === "remove" ? "Yes, remove" : confirm.type === "block" ? "Yes, block" : "Yes, unblock"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllGroupMembers;